/* eslint-disable no-console */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { useApi } from './useApi';

type ResolvedType = 'success' | 'failed';

type VendaWsEvent = {
  type: ResolvedType | 'pending';
  vendaId: string;
  status?: number;
};

type Options = {
  socket: Socket | null;
  vendaId?: string;
  vendaToken?: string;

  // WS
  wsEventName?: string; // default: 'venda-cc'
  subscribeEventName?: string; // default: 'venda:subscribe'
  unsubscribeEventName?: string; // default: 'venda:unsubscribe'
  wsSubscribePayload?: (args: { vendaId: string; vendaToken: string }) => any;

  // fallback
  fallbackEnabled?: boolean; // default true
  fallbackStartAfterMs?: number; // default 6000
  fallbackOnSocketError?: boolean; // default true (dispara no connect_error/reconnect_error)
  fallbackOnDisconnect?: boolean; // default false (se quiser, liga)
  pollIntervalMs?: number; // default 2500
  pollStopAfterMs?: number; // default 10min
  preferPolling?: boolean;

  // map status numérico -> resolved
  resolveFromStatus?: (status: number) => ResolvedType | null;

  onResolved?: (args: {
    type: ResolvedType;
    source: 'ws' | 'polling';
    data: VendaWsEvent | { vendaId: string; status: number };
  }) => void;

  debug?: boolean;
};

export function useVendaStatusObserver(opts: Options) {
  const api = useApi();

  const {
    socket,
    vendaId,
    vendaToken,
    wsEventName = 'venda-cc',
    subscribeEventName = 'venda:subscribe',
    unsubscribeEventName = 'venda:unsubscribe',
    wsSubscribePayload,

    fallbackEnabled = true,
    fallbackStartAfterMs = 6000,
    fallbackOnSocketError = true,
    fallbackOnDisconnect = false,

    pollIntervalMs = 2500,
    pollStopAfterMs = 10 * 60_000,
    preferPolling = false,

    resolveFromStatus = () => null,
    onResolved,
    debug = false,
  } = opts;

  const [resolvedType, setResolvedType] = useState<ResolvedType | null>(null);
  const [mode, setMode] = useState<'idle' | 'ws' | 'polling'>('idle');
  const [lastEvent, setLastEvent] = useState<any>(null);

  const settledRef = useRef(false);
  const pollingStartedRef = useRef(false);

  const pollTimerRef = useRef<any>(null);
  const fallbackTimerRef = useRef<any>(null);
  const startedAtRef = useRef<number>(0);

  const canRun = useMemo(
    () => !!socket && !!vendaId && !!vendaToken,
    [socket, vendaId, vendaToken],
  );

  const log = (...args: any[]) =>
    debug && console.log('[useVendaStatusObserver]', ...args);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearFallbackTimer = () => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  };

  const settle = (
    type: ResolvedType,
    source: 'ws' | 'polling',
    data: any,
  ) => {
    if (settledRef.current) return;
    settledRef.current = true;

    setResolvedType(type);
    setLastEvent(data);
    setMode(source);

    log('settle', { type, source, data });
    onResolved?.({ type, source, data });

    // limpando tudo
    stopPolling();
    clearFallbackTimer();
  };

  const startPolling = (reason: string) => {
    if (!fallbackEnabled) return;
    if (!vendaToken) return;
    if (settledRef.current) return;

    // guard pra não iniciar 2x (timer + connect_error, por ex.)
    if (pollingStartedRef.current) return;
    pollingStartedRef.current = true;

    stopPolling();
    clearFallbackTimer();
    setMode('polling');
    log('startPolling', { reason });

    const tick = async () => {
      if (settledRef.current) return;

      if (Date.now() - startedAtRef.current > pollStopAfterMs) {
        log('polling stop by timeout');
        stopPolling();
        return;
      }

      try {
        const r = await api.Venda.getPublicStatus(vendaToken);
        setLastEvent(r);

        const resolved = resolveFromStatus(r.status);
        if (resolved) {
          settle(resolved, 'polling', r);
          return;
        }
      } catch (e: any) {
        log('poll error', e?.message ?? e);
        // segue tentando
      }

      pollTimerRef.current = setTimeout(tick, pollIntervalMs);
    };

    tick();
  };

  useEffect(() => {
    // reset quando muda venda
    settledRef.current = false;
    pollingStartedRef.current = false;

    setResolvedType(null);
    setLastEvent(null);
    setMode('idle');

    stopPolling();
    clearFallbackTimer();

    if (!canRun) return;

    startedAtRef.current = Date.now();

    if (preferPolling) {
      startPolling('preferPolling=true');
      return () => stopPolling();
    }

    // WS handlers
    const subscribe = () => {
      if (!socket || !vendaId || !vendaToken) return;
      log('subscribe');
      socket?.emit(
        subscribeEventName,
        wsSubscribePayload?.({ vendaId, vendaToken }) ?? { vendaId, vendaToken },
        () => {},
      );
    };

    const onWsEvent = (data: VendaWsEvent) => {
      setLastEvent(data);
      if (!data || data.vendaId !== vendaId) return;

      if (data.type === 'success') settle('success', 'ws', data);
      if (data.type === 'failed') settle('failed', 'ws', data);
    };

    const onConnectError = (err: any) => {
      setLastEvent({ type: 'connect_error', message: err?.message });
      log('connect_error', err?.message ?? err);

      if (fallbackOnSocketError) startPolling('connect_error');
    };

    const onReconnectError = (err: any) => {
      setLastEvent({ type: 'reconnect_error', message: err?.message });
      log('reconnect_error', err?.message ?? err);

      if (fallbackOnSocketError) startPolling('reconnect_error');
    };

    const onDisconnect = (reason: any) => {
      setLastEvent({ type: 'disconnect', reason });
      log('disconnect', reason);

      if (fallbackOnDisconnect) startPolling(`disconnect:${String(reason)}`);
    };

    setMode('ws');

    socket?.on('connect', subscribe);
    socket?.on('reconnect', subscribe);
    socket?.on(wsEventName, onWsEvent);

    socket?.on('connect_error', onConnectError);
    socket?.io?.on?.('reconnect_error', onReconnectError);
    socket?.on('disconnect', onDisconnect);

    if (socket?.connected) subscribe();

    if (fallbackEnabled) {
      fallbackTimerRef.current = setTimeout(() => {
        if (!settledRef.current) startPolling('fallbackStartAfterMs');
      }, fallbackStartAfterMs);
    }

    return () => {
      try {
        if (socket && vendaId) socket?.emit(unsubscribeEventName, { vendaId });
      } catch {}

      socket?.off('connect', subscribe);
      socket?.off('reconnect', subscribe);
      socket?.off(wsEventName, onWsEvent);

      socket?.off('connect_error', onConnectError);
      socket?.io?.off?.('reconnect_error', onReconnectError);
      socket?.off('disconnect', onDisconnect);

      stopPolling();
      clearFallbackTimer();
    };
  }, [canRun, vendaId, vendaToken, socket, preferPolling]);

  return { resolvedType, mode, lastEvent };
}
